// Route (Descubrir) branch coherence — the ONE rule that keeps a `route` node's
// `config.options` (what the classifier may emit), its outgoing edges (where
// each key goes) and its trailing default branch in agreement.
//
// WHY IT LIVES HERE. The flow editor grew this rule inline (FlowEditorScreen's
// `writeRoute`) and the server had no version of it at all, so any server-side
// generator would have shipped a SECOND coherence rule — the recon files that as
// DRIFT 6. Both consumers now import this file: the editor's category form and
// the capture-flow generator produce structurally identical routes or the parity
// test in route-branch.test.ts fails.
//
// PURE + web-safe: its own subpath (`@vysto/shared/bot-config/route-branch`), no
// barrel import, no React, no DB — the web bundles it, the API and the engine
// import the same file.
//
// THE ORDER IS THE SEMANTICS. `pickBranchEdge` (engine run-skill.ts) takes the
// FIRST outgoing edge whose `when` matches, and treats a missing/`'true'` `when`
// as always matching. A route's default branch must therefore stay LAST among
// that node's outgoing edges: an edge added after it is unreachable, silently.
// Every function here maintains that invariant.

import {
  DEFAULT_BRANCH,
  type StarterEdge,
  type StarterFlow,
  type StarterNode,
  whenForIntent,
} from './orchestrator-scaffold.js'

/** One author-defined Descubrir category paired with where it routes. The
 *  `key`/`label` live on the node's `config.options` (the classifier reads the
 *  labels); the `to` lives on the matching outgoing edge. */
export interface RouteBranchRow {
  key: string
  label: string
  to: string
  /** LO QUE LA CATEGORÍA SIGNIFICA, para el clasificador (F5-2). La etiqueta es
   *  lo que el menú DICE; la pista es lo que el modelo lee para reconocerla
   *  aunque la persona la diga con otras palabras. Ausente = no hay nada más
   *  que decir que la etiqueta, y la opción se escribe como siempre. */
  hint?: string
}

/** Pull the option key out of a route-branch condition like
 *  `vars.intent == "agendar"`. Returns DEFAULT_BRANCH for a bare/`true`
 *  condition (the "any other case" branch), or undefined for an expression we
 *  cannot read (a hand-written one — preserved, never rewritten). */
export function intentOfWhen(when: string | undefined): string | undefined {
  if (!when || when.trim() === 'true' || when.trim() === '') return DEFAULT_BRANCH
  // `[^"]*` (not `+`) so a freshly-added, not-yet-named category (`vars.intent
  // == ""`) still parses to an empty key and shows as an editable row, instead
  // of being treated as a hand-written condition and hidden.
  const m = /==\s*"([^"]*)"/.exec(when)
  return m ? m[1] : undefined
}

/** The variable a route node classifies into. `intent` is the scaffold's name
 *  and the fallback for a node that never declared one. */
export function routeVarOf(node: StarterNode): string {
  const v = node.config.var
  return typeof v === 'string' && v.trim() !== '' ? v : 'intent'
}

/** The author-defined categories stored on a `route` node's `config.options`. */
export function routeOptionsOf(node: StarterNode): { key: string; label: string; hint?: string }[] {
  const o = node.config.options
  return Array.isArray(o) ? (o as { key: string; label: string; hint?: string }[]) : []
}

/** La PISTA que la opción de esta clave lleva HOY en el nodo (F5-2), o
 *  `undefined` cuando la opción no existe o se escribió antes de que las
 *  pistas existieran. Se lee junto a la etiqueta y no aparte: las dos son la
 *  misma opción vista por sus dos lectores (el menú y el clasificador). */
export function routeOptionHintOf(node: StarterNode, key: string): string | undefined {
  return routeOptionsOf(node).find((o) => o.key === key)?.hint
}

/** Read a route node's categories (one per parseable non-default outgoing edge)
 *  plus the destination of its "en cualquier otro caso" default branch.
 *  Unparseable (hand-written) conditions are skipped here and preserved by
 *  `writeRouteBranches`. */
export function routeRowsOf(
  node: StarterNode,
  edges: readonly StarterEdge[],
): { rows: RouteBranchRow[]; defaultTo: string } {
  const opts = routeOptionsOf(node)
  const rows: RouteBranchRow[] = []
  let defaultTo = ''
  for (const e of edges.filter((x) => x.from === node.id)) {
    const key = intentOfWhen(e.when)
    if (key === DEFAULT_BRANCH) {
      defaultTo = e.to
      continue
    }
    if (key === undefined) continue
    const opt = opts.find((o) => o.key === key)
    rows.push({
      key,
      label: opt?.label ?? key,
      to: e.to,
      // LA PISTA VIAJA CON LA FILA (F5-2). El editor lee las categorías con esta
      // función y las vuelve a escribir enteras con `writeRouteBranches`: una
      // fila que no la traiga BORRA el significado del proceso en cuanto alguien
      // abra el formulario de categorías, en silencio y sin haberlo editado.
      ...(opt?.hint === undefined ? {} : { hint: opt.hint }),
    })
  }
  return { rows, defaultTo }
}

/**
 * Rebuild a route node's categories from ONE list of rows: `config.options` and
 * the outgoing edges are written together, the default branch always lands last,
 * and any hand-written condition on the node survives ahead of the managed set.
 *
 * This is the editor's category form in function shape — the whole list is the
 * unit of edit there, so the whole list is the unit here.
 */
export function writeRouteBranches(
  flow: StarterFlow,
  routeNodeId: string,
  rows: readonly RouteBranchRow[],
  defaultTo: string,
): StarterFlow {
  const node = flow.nodes.find((n) => n.id === routeNodeId)
  if (!node) return flow
  const routeVar = routeVarOf(node)
  // Preserve any outgoing edge we don't manage (neither a parseable category
  // nor the default branch) so a power-user expression isn't dropped.
  const preserved = flow.edges.filter(
    (e) => e.from === routeNodeId && intentOfWhen(e.when) === undefined,
  )
  const managed: StarterEdge[] = [
    ...rows.map((r) => ({ from: routeNodeId, to: r.to, when: whenForIntent(routeVar, r.key) })),
    ...(defaultTo ? [{ from: routeNodeId, to: defaultTo, when: 'true' }] : []),
  ]
  const options = rows.map((r) => ({
    key: r.key,
    label: r.label,
    ...(r.hint === undefined ? {} : { hint: r.hint }),
  }))
  const nodes = flow.nodes.map((n) =>
    n.id === routeNodeId ? { ...n, config: { ...n.config, options } } : n,
  )
  const edges = [...flow.edges.filter((e) => e.from !== routeNodeId), ...preserved, ...managed]
  return { nodes, edges }
}

/**
 * Add ONE category to a route node, in place.
 *
 * Idempotent by `key`: a second run with the same key updates the option's label
 * and the edge's destination instead of adding a twin (the generator re-runs
 * every time somebody regenerates a process's capture flow).
 *
 * Unlike `writeRouteBranches` this does NOT re-lay the node's other edges — a
 * generator editing the author's main flow moves as little as possible, so the
 * "deshacer" diff reads as the one change it made. The new edge is inserted
 * immediately BEFORE the node's trailing default branch; without that it would
 * sit after an always-matching `when` and never fire.
 */
export function addRouteBranch(
  flow: StarterFlow,
  routeNodeId: string,
  branch: RouteBranchRow,
): StarterFlow {
  const node = flow.nodes.find((n) => n.id === routeNodeId)
  if (!node) return flow
  if (branch.key === DEFAULT_BRANCH || branch.key.trim() === '') {
    throw new Error(`addRouteBranch: "${branch.key}" no es una clave de categoría válida.`)
  }
  const when = whenForIntent(routeVarOf(node), branch.key)

  // config.options — upsert by key, order preserved. La PISTA se escribe con la
  // etiqueta (F5-2) y se QUITA cuando la rama ya no trae una: las dos son la
  // misma opción, y dejar la pista vieja pegada a una etiqueta nueva es
  // exactamente el desfase que el detector de deriva tendría que reclamar.
  const options = routeOptionsOf(node)
  const written = {
    key: branch.key,
    label: branch.label,
    ...(branch.hint === undefined ? {} : { hint: branch.hint }),
  }
  const optIdx = options.findIndex((o) => o.key === branch.key)
  const nextOptions =
    optIdx >= 0 ? options.map((o, i) => (i === optIdx ? written : o)) : [...options, written]
  const nodes = flow.nodes.map((n) =>
    n.id === routeNodeId ? { ...n, config: { ...n.config, options: nextOptions } } : n,
  )

  // The edge — update the existing one for this key, or insert a new one.
  const existingIdx = flow.edges.findIndex(
    (e) => e.from === routeNodeId && intentOfWhen(e.when) === branch.key,
  )
  if (existingIdx >= 0) {
    const edges = flow.edges.map((e, i) => (i === existingIdx ? { ...e, to: branch.to, when } : e))
    return { nodes, edges }
  }
  const next: StarterEdge = { from: routeNodeId, to: branch.to, when }
  const defaultIdx = flow.edges.findIndex(
    (e) => e.from === routeNodeId && intentOfWhen(e.when) === DEFAULT_BRANCH,
  )
  // No default branch on this node: sit after its last outgoing edge (keeping
  // the node's branches together), or at the end when it has none at all.
  let at = defaultIdx
  if (at < 0) {
    let last = -1
    flow.edges.forEach((e, i) => {
      if (e.from === routeNodeId) last = i
    })
    at = last >= 0 ? last + 1 : flow.edges.length
  }
  const edges = [...flow.edges.slice(0, at), next, ...flow.edges.slice(at)]
  return { nodes, edges }
}
